import {
  ARRAY_LITERAL,
  ATTRIBUTE,
  DECORATOR,
  DELEGATE_FLAG,
  BINDING_FLAG,
  INTERPOLATOR,
  TRANSITION,
  EVENT
} from "../../../config/types";
import getLowestIndex from "../utils/getLowestIndex";
import readMustache from "../readMustache";
import { decodeCharacterReferences } from "src/utils/html";
import readExpressionList from "../expressions/shared/readExpressionList";
import readExpressionOrReference from "../readExpressionOrReference";
import flattenExpression from "parse/utils/flattenExpression";
import refineExpression from "parse/utils/refineExpression";
import { isString } from "utils/is";

const attributeNamePattern = /^[^\s"'>\/=]+/;
const onPattern = /^on/;
const eventPattern = /^on-([a-zA-Z\*\.$_]((?:[a-zA-Z\*\.$_0-9\-]|\\-)+))$/;
const reservedEventNames = /^(?:change|reset|teardown|update|construct|config|init|render|complete|unrender|detach|insert|destruct|attachchild|detachchild)$/;
const decoratorPattern = /^as-([a-z-A-Z][-a-zA-Z_0-9]*)$/;
const transitionPattern = /^([a-zA-Z](?:(?!-in-out)[-a-zA-Z_0-9])*)-(in|out|in-out)$/;
const boundPattern = /^((bind|class)-(([-a-zA-Z0-9_])+))$/;
const directives = {
  lazy: { t: BINDING_FLAG, v: "l" },
  twoway: { t: BINDING_FLAG, v: "t" },
  "no-delegation": { t: DELEGATE_FLAG }
};
const unquotedAttributeValueTextPattern = /^[^\s"'=<>\/`]+/;
const proxyEvent = /^[^\s"'=<>@\[\]()]*/;
const whitespace = /^\s+/;

const slashes = /\\/g;
function splitEvent(str) {
  const result = [];
  let s = 0;

  for (let i = 0; i < str.length; i++) {
    if (str[i] === "-" && str[i - 1] !== "\\") {
      result.push(str.substring(s, i).replace(slashes, ""));
      s = i + 1;
    }
  }

  result.push(str.substring(s).replace(slashes, ""));

  return result;
}

export default function readAttribute(parser) {
  let name, i, nearest, idx;

  parser.sp();

  name = parser.matchPattern(attributeNamePattern);
  if (!name) {
    return null;
  }

  // check for accidental delimiter consumption e.g. <tag bool{{>attrs}} />
  nearest = name.length;
  for (i = 0; i < parser.tags.length; i++) {
    if (~(idx = name.indexOf(parser.tags[i].open))) {
      if (idx < nearest) nearest = idx;
    }
  }
  if (nearest < name.length) {
    parser.pos -= name.length - nearest;
    name = name.substr(0, nearest);
    if (!name) return null;
  }

  return { n: name };
}

function readAttributeValue(parser) {
  const start = parser.pos;

  // next character must be `=`, `/`, `>` or whitespace
  if (!/[=\/>\s]/.test(parser.nextChar())) {
    parser.error("Expected `=`, `/`, `>` or whitespace");
  }

  parser.sp();

  if (!parser.matchString("=")) {
    parser.pos = start;
    return null;
  }

  parser.sp();

  const valueStart = parser.pos;
  const startDepth = parser.sectionDepth;

  const value =
    readQuotedAttributeValue(parser, `'`) ||
    readQuotedAttributeValue(parser, `"`) ||
    readUnquotedAttributeValue(parser);

  if (value === null) {
    parser.error("Expected valid attribute value");
  }

  if (parser.sectionDepth !== startDepth) {
    parser.pos = valueStart;
    parser.error(
      "An attribute value must contain as many opening section tags as closing section tags"
    );
  }

  if (!value.length) {
    return "";
  }

  if (value.length === 1 && isString(value[0])) {
    return decodeCharacterReferences(value[0]);
  }

  return value;
}

function readUnquotedAttributeValueToken(parser) {
  let text, index;

  const start = parser.pos;

  text = parser.matchPattern(unquotedAttributeValueTextPattern);

  if (!text) {
    return null;
  }

  const haystack = text;
  const needles = parser.tags.map(t => t.open); // TODO refactor... we do this in readText.js as well

  if ((index = getLowestIndex(haystack, needles)) !== -1) {
    text = text.substr(0, index);
    parser.pos = start + text.length;
  }

  return text;
}

function readUnquotedAttributeValue(parser) {
  parser.inAttribute = true;

  const tokens = [];

  let token = readMustache(parser) || readUnquotedAttributeValueToken(parser);
  while (token) {
    tokens.push(token);
    token = readMustache(parser) || readUnquotedAttributeValueToken(parser);
  }

  if (!tokens.length) {
    return null;
  }

  parser.inAttribute = false;
  return tokens;
}

function readQuotedAttributeValue(parser, quoteMark) {
  const start = parser.pos;

  if (!parser.matchString(quoteMark)) {
    return null;
  }

  parser.inAttribute = quoteMark;

  const tokens = [];

  let token = readMustache(parser) || readQuotedStringToken(parser, quoteMark);
  while (token !== null) {
    tokens.push(token);
    token = readMustache(parser) || readQuotedStringToken(parser, quoteMark);
  }

  if (!parser.matchString(quoteMark)) {
    parser.pos = start;
    return null;
  }

  parser.inAttribute = false;

  return tokens;
}

function readQuotedStringToken(parser, quoteMark) {
  const haystack = parser.remaining();

  const needles = parser.tags.map(t => t.open); // TODO refactor... we do this in readText.js as well
  needles.push(quoteMark);

  const index = getLowestIndex(haystack, needles);

  if (index === -1) {
    parser.error("Quoted attribute value must have a closing quote");
  }

  if (!index) {
    return null;
  }

  parser.pos += index;
  return haystack.substr(0, index);
}

export function readAttributeOrDirective(parser) {
  let match, directive;

  const attribute = readAttribute(parser, false);

  if (!attribute) return null;

  // lazy, twoway
  if ((directive = directives[attribute.n])) {
    attribute.t = directive.t;
    if (directive.v) attribute.v = directive.v;
    delete attribute.n; // no name necessary
    parser.sp();
    if (parser.nextChar() === "=") attribute.f = readAttributeValue(parser);
  } else if ((match = decoratorPattern.exec(attribute.n))) {
    // decorators
    attribute.n = match[1];
    attribute.t = DECORATOR;
    readArguments(parser, attribute);
  } else if ((match = transitionPattern.exec(attribute.n))) {
    // transitions
    attribute.n = match[1];
    attribute.t = TRANSITION;
    readArguments(parser, attribute);
    attribute.v =
      match[2] === "in-out" ? "t0" : match[2] === "in" ? "t1" : "t2";
  } else if ((match = eventPattern.exec(attribute.n))) {
    // on-click etc
    attribute.n = splitEvent(match[1]);
    attribute.t = EVENT;

    parser.inEvent = true;

    // check for a proxy event
    if (!readProxyEvent(parser, attribute)) {
      // otherwise, it's an expression
      readArguments(parser, attribute, true);
    } else if (reservedEventNames.test(attribute.f)) {
      parser.pos -= attribute.f.length;
      parser.error(
        "Cannot use reserved event names (change, reset, teardown, update, construct, config, init, render, unrender, complete, detach, insert, destruct, attachchild, detachchild)"
      );
    }

    parser.inEvent = false;
  } else if ((match = boundPattern.exec(attribute.n))) {
    // bound directives
    const bind = match[2] === "bind";
    attribute.n = bind ? match[3] : match[1];
    attribute.t = ATTRIBUTE;
    readArguments(parser, attribute, false, true);

    if (!attribute.f && bind) {
      attribute.f = [{ t: INTERPOLATOR, r: match[3] }];
    }
  } else {
    parser.sp();
    const value = parser.nextChar() === "=" ? readAttributeValue(parser) : null;
    attribute.f = value != null ? value : attribute.f;

    if (parser.sanitizeEventAttributes && onPattern.test(attribute.n)) {
      return { exclude: true };
    } else {
      attribute.f = attribute.f || (attribute.f === "" ? "" : 0);
      attribute.t = ATTRIBUTE;
    }
  }

  return attribute;
}

function readProxyEvent(parser, attribute) {
  const start = parser.pos;
  if (!parser.matchString("="))
    parser.error(`Missing required directive arguments`);

  const quote = parser.matchString(`'`) || parser.matchString(`"`);
  parser.sp();
  const proxy = parser.matchPattern(proxyEvent);

  if (proxy !== undefined) {
    if (quote) {
      parser.sp();
      if (!parser.matchString(quote)) parser.pos = start;
      else return (attribute.f = proxy) || true;
    } else if (!parser.matchPattern(whitespace)) {
      parser.pos = start;
    } else {
      return (attribute.f = proxy) || true;
    }
  } else {
    parser.pos = start;
  }
}

function readArguments(parser, attribute, required = false, single = false) {
  parser.sp();
  if (!parser.matchString("=")) {
    if (required) parser.error(`Missing required directive arguments`);
    return;
  }
  parser.sp();

  const quote = parser.matchString('"') || parser.matchString("'");
  const spread = parser.spreadArgs;
  parser.spreadArgs = true;
  parser.inUnquotedAttribute = !quote;
  const expr = single
    ? readExpressionOrReference(parser, [quote || " ", "/", ">"])
    : { m: readExpressionList(parser), t: ARRAY_LITERAL };
  parser.inUnquotedAttribute = false;
  parser.spreadArgs = spread;

  if (quote) {
    parser.sp();
    if (parser.matchString(quote) !== quote)
      parser.error(`Expected matching quote '${quote}'`);
  }

  if (single) {
    const interpolator = { t: INTERPOLATOR };
    refineExpression(expr, interpolator);
    attribute.f = [interpolator];
  } else {
    attribute.f = flattenExpression(expr);
  }
}
